import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests. Firebase ID tokens expire after ~1 hour, so prefer a
// fresh token from the current Firebase user (getIdToken() returns the cached
// token and only refreshes when it's expired/near expiry). This prevents stale
// tokens from 401-ing requests — especially several image uploads at once.
api.interceptors.request.use(
  async (config) => {
    let token = localStorage.getItem('token');
    try {
      const { auth } = await import('../firebase');
      if (auth.currentUser) {
        token = await auth.currentUser.getIdToken();
        localStorage.setItem('token', token);
      }
    } catch (e) {
      // Fall back to the stored token if Firebase isn't ready.
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle responses and errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only logout on 401 from auth endpoints, not payment/stripe errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      const url = error.config?.url || '';

      // Try to refresh Firebase token before logging out
      try {
        const { auth } = await import('../firebase');
        const user = auth.currentUser;

        if (user) {
          console.log('🔄 Refreshing expired Firebase token...');
          const newToken = await user.getIdToken(true); // Force refresh
          localStorage.setItem('token', newToken);

          // Retry the original request with new token
          originalRequest._retry = true;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.error('❌ Token refresh failed:', refreshError);
      }

      // Only logout if it's an auth-related endpoint and refresh failed
      if (url.includes('/auth/') || url.includes('/users/me')) {
        console.log('🚪 Logging out due to auth failure');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
      // For other 401s (Stripe, etc), just let the error bubble up
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  updateDetails: (data) => api.put('/auth/updatedetails', data),
  updatePassword: (data) => api.put('/auth/updatepassword', data),
  forgotPassword: (email) => api.post('/auth/forgotpassword', { email }),
  resetPassword: (token, password) => api.put(`/auth/resetpassword/${token}`, { password }),
};

// User endpoints
export const userAPI = {
  getUsers: (params) => api.get('/users', { params }),
  getUser: (id) => api.get(`/users/${id}`),
  getUserByUsername: (username) => api.get(`/users/username/${username}`),
  updateUser: (id, data) => api.put(`/users/${id}`, data),
  deleteUser: (id) => api.delete(`/users/${id}`),
  followUser: (id) => api.post(`/users/${id}/follow`),
  unfollowUser: (id) => api.post(`/users/${id}/unfollow`),
  getFollowers: (id) => api.get(`/users/${id}/followers`),
  getFollowing: (id) => api.get(`/users/${id}/following`),
  getUserListings: (id, params) => api.get(`/users/${id}/listings`, { params }),
  getSavedItems: (id) => api.get(`/users/${id}/saved-items`),
  getUserStats: () => api.get('/users/stats/summary'),
  suspendUser: (id, reason) => api.put(`/users/${id}/suspend`, { reason }),
  unsuspendUser: (id) => api.put(`/users/${id}/unsuspend`),
  verifyUser: (id) => api.put(`/users/${id}/verify`),
  makeAdmin: (id) => api.put(`/users/${id}/make-admin`),
  removeAdmin: (id) => api.put(`/users/${id}/remove-admin`),
  adminResetPassword: (id, newPassword) => api.put(`/users/${id}/reset-password`, { newPassword }),
  // Stripe Connect payouts
  stripeConnectOnboarding: () => api.post('/users/stripe-connect-onboarding'),
  getStripeConnectStatus: () => api.get('/users/stripe-connect-status'),
  stripeConnectLoginLink: () => api.post('/users/stripe-connect-login'),
  getInstantBalance: () => api.get('/users/stripe-instant-balance'),
  requestInstantPayout: () => api.post('/users/stripe-instant-payout'),
};

// Baby items endpoints
export const itemAPI = {
  getItems: (params) => api.get('/baby-items', { params }),
  getItem: (id) => api.get(`/baby-items/${id}`),
  createItem: (data) => api.post('/baby-items', data),
  updateItem: (id, data) => api.put(`/baby-items/${id}`, data),
  deleteItem: (id) => api.delete(`/baby-items/${id}`),
  likeItem: (id) => api.post(`/baby-items/${id}/like`),
  unlikeItem: (id) => api.post(`/baby-items/${id}/unlike`),
  saveItem: (id) => api.post(`/baby-items/${id}/save`),
  unsaveItem: (id) => api.post(`/baby-items/${id}/unsave`),
  bulkUpdateItems: (data) => api.put('/baby-items/bulk-update', data),
  bulkDeleteItems: (data) => api.delete('/baby-items/bulk-delete', data),
  getCategories: () => api.get('/baby-items/categories'),
  getAgeGroups: () => api.get('/baby-items/age-groups'),
  getItemStats: () => api.get('/baby-items/stats/summary'),
  featureItem: (id) => api.put(`/baby-items/${id}/feature`),
  archiveItem: (id) => api.put(`/baby-items/${id}/archive`),
};

// Transaction endpoints
export const transactionAPI = {
  getTransactions: (params) => api.get('/transactions', { params }),
  getAllTransactions: (params) => api.get('/transactions/admin/all', { params }),
  getTransaction: (id) => api.get(`/transactions/${id}`),
  createTransaction: (data) => api.post('/transactions', data),
  updateTransaction: (id, data) => api.put(`/transactions/${id}`, data),
  refundTransaction: (id, data) => api.post(`/transactions/${id}/refund`, data),
  getReceipt: (id) => api.get(`/transactions/${id}/receipt`),
  rateTransaction: (id, data) => api.post(`/transactions/${id}/rate`, data),
  getTransactionStats: () => api.get('/transactions/stats/summary'),
  retryShippingLabel: (id) => api.post(`/shipping/retry-label/${id}`),
  retryPayout: (id) => api.post(`/payments/retry-payout/${id}`),
};

// Payment endpoints
export const paymentAPI = {
  // Legacy Stripe payments
  createPaymentIntent: (data) => api.post('/payments/create-intent', data),
  createTransaction: (data) => api.post('/payments/create-transaction', data),
  markShipped: (id, data) => api.post(`/payments/mark-shipped/${id}`, data),
  confirmDelivery: (id) => api.post(`/payments/confirm-delivery/${id}`),
  openDispute: (id, data) => api.post(`/payments/dispute/${id}`, data),
  calculateFees: (itemId) => api.get(`/payments/calculate-fees/${itemId}`),
  getRevenueSummary: () => api.get('/payments/revenue-summary'),
  processAutoReleases: () => api.post('/payments/process-auto-releases'),
  manualReleaseFunds: (id) => api.post(`/payments/release-funds/${id}`),

  // Escrow.com payments
  createEscrowTransaction: (data) => api.post('/payments/escrow/create-transaction', data),
  markEscrowShipped: (id, data) => api.post(`/payments/escrow/mark-shipped/${id}`, data),
  acceptEscrowItem: (id) => api.post(`/payments/escrow/accept-item/${id}`),
  rejectEscrowItem: (id, data) => api.post(`/payments/escrow/reject-item/${id}`, data),
  getEscrowStatus: (id) => api.get(`/payments/escrow/status/${id}`),

  // Payment config
  getConfig: () => api.get('/payments/config'),

  // Rating (deprecated - use transactionAPI.rateTransaction instead)
  submitRating: (id, data) => api.post(`/transactions/${id}/rate`, data),
};

// Message endpoints
export const messageAPI = {
  getConversations: () => api.get('/messages'),
  getConversation: (userId) => api.get(`/messages/${userId}`),
  sendMessage: (data) => api.post('/messages', data),
  getUnreadCount: () => api.get('/messages/unread/count'),
  markAsRead: (conversationId) => api.put(`/messages/conversations/${conversationId}/read`),
};

// Notification endpoints
export const notificationAPI = {
  getNotifications: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/mark-all-read'),
  deleteNotification: (id) => api.delete(`/notifications/${id}`),
  clearReadNotifications: () => api.delete('/notifications/clear-read'),
};

// Theme endpoints
export const themeAPI = {
  // Get current active theme
  getCurrentTheme: () => api.get('/themes/current'),

  // Fallback - get active theme
  getActiveTheme: () => api.get('/themes/active'),

  // Get all themes (predefined + custom)
  getThemes: () => api.get('/themes'),

  // Get only predefined themes
  getPredefinedThemes: () => api.get('/themes/predefined'),

  // Get single theme
  getTheme: (id) => api.get(`/themes/${id}`),

  // Activate theme by database ID
  activateTheme: (id) => api.post(`/themes/${id}/activate`),

  // Activate theme by name (for predefined themes)
  activateThemeByName: (themeName) => api.post('/themes/activate-by-name', { themeName }),

  // Activate current seasonal theme
  activateSeasonalTheme: () => api.post('/themes/activate-seasonal'),

  // Seed predefined themes
  seedThemes: () => api.post('/themes/seed'),

  // CRUD
  createTheme: (data) => api.post('/themes', data),
  updateTheme: (id, data) => api.put(`/themes/${id}`, data),
  deleteTheme: (id) => api.delete(`/themes/${id}`),

  // Force update predefined themes with latest colors
  updatePredefinedThemes: () => api.post('/themes/update-predefined')
};

// Upload endpoints - Updated for Cloudinary
export const uploadAPI = {
  // Upload single image
  uploadImage: async (formDataOrFile) => {
    // Accept either a File object or FormData
    let formData;
    if (formDataOrFile instanceof FormData) {
      formData = formDataOrFile;
    } else {
      formData = new FormData();
      formData.append('image', formDataOrFile);
    }

    return api.post('/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Upload multiple images
  uploadMultiple: async (files) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('images', file);
    });

    return api.post('/upload/multiple', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Upload profile image
  uploadProfile: async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('type', 'profile');

    return api.post('/upload/profile', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Delete image
  deleteImage: (publicId) =>
    api.delete('/upload/image', { data: { publicId } }),
};

// Admin endpoints
export const adminAPI = {
  getDashboardStats: () => api.get('/admin/dashboard/stats'),
  getRecentActivity: (params) => api.get('/admin/dashboard/activity', { params }),
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  getEndpoints: () => api.get('/admin/api-tools/endpoints'),
  testEndpoint: (data) => api.post('/admin/api-tools/test', data),
  resetActivity: () => api.post('/admin/reset-activity', { confirm: 'RESET' }),

  // Promo placement management
  getPromoSlots: () => api.get('/admin/promos/slots'),
  listPromos: (params) => api.get('/admin/promos', { params }),
  getPromo: (id) => api.get(`/admin/promos/${id}`),
  createPromo: (data) => api.post('/admin/promos', data),
  updatePromo: (id, data) => api.put(`/admin/promos/${id}`, data),
  deletePromo: (id) => api.delete(`/admin/promos/${id}`),
  togglePromo: (id) => api.patch(`/admin/promos/${id}/toggle`),

  // Shipping label backlog
  getLabelStats: () => api.get('/admin/labels/stats'),

  // Item reports / moderation
  getReports: (params) => api.get('/admin/reports', { params }),
  getReportStats: () => api.get('/admin/reports/stats'),
  getReport: (id) => api.get(`/admin/reports/${id}`),
  updateReport: (id, data) => api.put(`/admin/reports/${id}`, data),
};

// Reporting (any authenticated user can report a listing)
export const reportAPI = {
  createReport: (data) => api.post('/reports', data),
};

// Public promo endpoints
export const promoAPI = {
  getActiveBySlot: (slotId) => api.get('/promos/active', { params: { slotId } }),
  trackImpression: (id) => api.post(`/promos/${id}/impression`),
  trackClick: (id) => api.post(`/promos/${id}/click`),
};

export default api;
